/**
 * Die Wahl „Absenden nach Stille" — EIN Baustein für die Einstellungen und
 * das Blatt der Antwortlänge. (29.09.2026)
 *
 * Martin, 29.09. ~22:00: die Einstellung soll auch dort stehen, wo Kurz,
 * Mittel und Lang gewählt wird. Damit sie nicht zweimal gebaut wird (und
 * einmal anders heißt), liegen hier die Werte, die Beschriftung, die Frage
 * „gibt es Auto?" und die Knopfreihe — die Einstellungen und das Blatt
 * rufen nur diese Funktion.
 *
 * ⚠️ EIN Besitzer: der Wert ist `sendeStilleMs` (Server-Wahrheit, Ticket
 *    2511). Diese Wahl hat keinen eigenen Zustand — sie zeigt, was man ihr
 *    per `zeige` gibt; gespeichert wird vom Aufrufer.
 * ⚠️ „Auto" steht nur für Konto 1 zur Wahl (`stilleAutoMoeglich`), wie der
 *    Server die Frage an Jev erlaubt (server/stille-auto-routen.ts). Anderen
 *    stünde ein Knopf, der stillschweigend die feste Frist nimmt.
 */
import {
  FEST_STILLE_STUFEN,
  FEST_STILLE_VORGABE_MS,
  STILLE_AUTO_MS,
  absendenModus,
  stilleKurz,
  type AbsendenModus,
} from "../../shared/stille-auto.js";
import { sendeStilleStufen } from "./settings.js";
import { jevMoeglich } from "./jev-konto.js";
import { baueStufenWahl, type StufenWahl } from "./stufen-wahl.js";

/** Der Name der Wahl — Beschriftung für Vorleser, Blatt und Einstellungen. */
export const STILLE_WAHL_NAME = "Absenden nach Stille";

/** Der Abschnitt der Einstellungsseite „Sprechen“, in dem die Wahl wohnt — Überschrift dort, Sprungziel aus dem Blatt. */
export const STILLE_ABSCHNITT = "Von selbst absenden";

/**
 * Gibt es „Auto" für dieses Konto? Nur das Betreiberkonto (1) — die
 * Mitschrift geht an einen fremden Dienst, den nur es freigegeben hat.
 * Die Konto-Regel selbst hat EINEN Besitzer: `jevMoeglich()` in jev-konto.ts.
 */
export function stilleAutoMoeglich(): boolean {
  return jevMoeglich();
}

/**
 * Die Stufe, die für `ms` gilt: die gleiche, sonst die nächste. Ein
 * gespeicherter Wert ohne eigenen Knopf steht auf der nächsten, statt dass
 * keine Stufe gilt. (Die Werte ohne Knopf über 3 s — 4 s, 5 s, 7 s, 12 s —
 * sind beim Lesen schon auf die nächstkürzere Stufe gerückt,
 * sendeStilleLesen in shared/stille-auto.ts; hier
 * bleibt der Rückhalt für alles Übrige, etwa 2,5 s.)
 */
function stilleStufeFuer(ms: number, stufen: readonly number[]): number {
  if (stufen.includes(ms)) return ms;
  // „Auto" ist keine Sekundenzahl: ein Wert ohne Knopf rückt nur auf Sekunden.
  const zeiten = stufen.filter((s) => s >= 0);
  return zeiten.reduce((a, s) => (Math.abs(s - ms) < Math.abs(a - ms) ? s : a), zeiten[0] ?? 0);
}

interface StilleWahl {
  el: HTMLElement;
  /** Markiert die Stufe, die für `ms` gilt. */
  zeige(ms: number): void;
}

/**
 * Die Knopfreihe: Aus, 2, 3, 5, 10, 30, 60 s und — für das Betreiberkonto — Auto. Die
 * Wahl meldet `waehle(ms)`; was gilt, zeigt `zeige(ms)`.
 */
export function baueStilleWahl(waehle: (ms: number) => void, mitAuto = stilleAutoMoeglich()): StilleWahl {
  const stufen = sendeStilleStufen(mitAuto);
  const wahl: StufenWahl<number> = baueStufenWahl<number>(
    STILLE_WAHL_NAME,
    stufen.map((ms) => ({ wert: ms, text: stilleKurz(ms) })),
    waehle,
  );
  // `data-ms`: der Wert steht am Knopf, wie ihn die Einstellungsseite
  // immer trug (Tests und Abgleich lesen ihn von dort).
  wahl.el.querySelectorAll<HTMLElement>(".einst-segment").forEach((b, i) => {
    b.dataset.ms = String(stufen[i]);
  });
  return {
    el: wahl.el,
    zeige(ms) {
      wahl.zeige(stilleStufeFuer(ms, stufen));
    },
  };
}

/*
 * ── Regler „Automatisch absenden“ (20.10.2026, Martin, Konto 1) ──
 *
 * *„einen weiteren Regler … damit ich diesen Jev-Auto-Absender abschalten kann, oder wo ich wählen kann: absenden
 * nach zwei Sekunden Stille … wenn ein Satz abgeschlossen ist“*. Drei Stufen: „Jev entscheidet“ (Auto, wie bisher),
 * „Feste Stille“ (2 / 3 / 5 s, nur nach abgeschlossenem Satz, ohne Jev) und „Aus“ (nur Knopf, Auslösewort oder
 * Abschied). Gilt für Sprechtaste und Auto-Mikro — beide lesen `sendeStilleMs`.
 *
 * ⚠️ EIN Besitzer: der Regler ist eine Sicht auf `sendeStilleMs` (shared/stille-auto.ts, absendenModus), kein
 *    zweites Feld. Was im Blatt der Antwortlänge gewählt wird, zeigt er genauso.
 * ⚠️ Nur wo „Auto“ zur Wahl steht (stilleAutoMoeglich, Konto 1) — ohne Jev gäbe es die erste Stufe nicht.
 */
export const ABSENDEN_NAME = "Automatisch absenden";
const ABSENDEN_STUFEN: ReadonlyArray<{ wert: AbsendenModus; text: string }> = [
  { wert: "jev", text: "Jev entscheidet" },
  { wert: "fest", text: "Feste Stille" },
  { wert: "aus", text: "Aus" },
];

/**
 * Schickt „Feste Stille“ nur nach abgeschlossenem Satz ab? Ja, wo der Regler steht (Konto 1). Andere Konten behalten
 * die feste Stille wie bisher. Gelesen bei jedem Takt (frontend/app.ts, pttSelbstSendenEinstellung).
 */
export function festNurNachSatz(): boolean {
  return stilleAutoMoeglich();
}

/**
 * Was für `ms` gilt, in den Worten der Einstellungsseite — für die Anzeige im Blatt „Antwort“ (22.10.2026,
 * lib/stille-blatt-zeile.ts). Wo der Regler steht (Konto 1): „Jev entscheidet“, „Aus“ oder „Feste Stille, 3 s“ — auch ein
 * Wert ohne eigenen Knopf (10, 30, 60 s) steht mit seiner echten Zahl da. Sonst die Stufe, die die Knopfreihe der
 * Einstellungen markiert (dieselbe Rundung wie `baueStilleWahl().zeige`).
 */
export function stilleStandWort(ms: number, mitAuto = stilleAutoMoeglich()): string {
  if (!mitAuto) return stilleKurz(stilleStufeFuer(ms, sendeStilleStufen(false)));
  const modus = absendenModus(ms);
  const wort = ABSENDEN_STUFEN.find((s) => s.wert === modus)?.text ?? "";
  return modus === "fest" ? `${wort}, ${stilleKurz(ms)}` : wort;
}

/** Der gespeicherte Wert für eine Stufe des Reglers; `bisher` behält eine feste Zahl, wenn „Feste Stille“ schon galt. */
export function absendenWert(modus: AbsendenModus, bisher: number): number {
  if (modus === "jev") return STILLE_AUTO_MS;
  if (modus === "aus") return 0;
  return absendenModus(bisher) === "fest" ? bisher : FEST_STILLE_VORGABE_MS;
}

interface AbsendenWahl {
  el: HTMLElement;
  /** Zeigt, was für `ms` gilt: die Stufe und — bei „Feste Stille“ — die Sekunden. */
  zeige(ms: number): void;
}

/** Zwei Reihen: die drei Stufen, darunter (nur bei „Feste Stille“) die Sekunden. Die Wahl meldet `waehle(ms)`. */
export function baueAbsendenWahl(waehle: (ms: number) => void): AbsendenWahl {
  let aktuell = 0;
  const el = document.createElement("div");
  el.className = "einst-absenden";
  const modus = baueStufenWahl<AbsendenModus>(ABSENDEN_NAME, ABSENDEN_STUFEN, (m) => waehle(absendenWert(m, aktuell)));
  const sekunden = baueStufenWahl<number>(
    "Feste Stille, nur nach abgeschlossenem Satz",
    FEST_STILLE_STUFEN.map((ms) => ({ wert: ms, text: stilleKurz(ms) })),
    waehle,
  );
  sekunden.el.classList.add("einst-absenden-sekunden");
  el.append(modus.el, sekunden.el);
  return {
    el,
    zeige(ms) {
      aktuell = ms;
      const m = absendenModus(ms);
      modus.zeige(m);
      sekunden.el.hidden = m !== "fest";
      // Ein fester Wert ohne eigenen Knopf (10, 30, 60 s aus dem Blatt) markiert keine Sekunde — ehrlich statt geraten.
      sekunden.zeige(FEST_STILLE_STUFEN.includes(ms) ? ms : null);
    },
  };
}
